'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Brain, Zap, Activity, BarChart2, Cpu, FlaskConical, Map, Waves, BookOpen, CheckCircle2, Trophy, History, Dumbbell, Shield } from 'lucide-react';

interface TourStep {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  tip?: string;
  color: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 1,
    icon: <Brain size={28} />,
    title: 'Welcome to iBrain LAB Cogni Dashboard',
    description: 'Your real-time EEG cognitive monitoring platform — streaming live brainwave data and translating it into actionable clinical insights, AI analysis, and gamified brain training.',
    tip: 'All panels are collapsible — click any section header to expand or collapse it.',
    color: '#F97316',
  },
  {
    id: 2,
    icon: <Activity size={28} />,
    title: 'Session Recording & Cloud Persistence',
    description: 'Start and stop EEG recording sessions. Every session is automatically saved to the cloud — including metrics, AI analysis, and biomarker results — for longitudinal tracking.',
    tip: 'Sessions are exported as EDF+ files and persisted to Supabase for multi-session comparison.',
    color: '#F97316',
  },
  {
    id: 3,
    icon: <History size={28} />,
    title: 'Session History & Cognitive Trends',
    description: 'Review all past sessions with longitudinal sparklines for cognitive load, attention, coherence, and band powers. Compare up to 5 sessions side-by-side on the trend chart.',
    tip: 'Expand any session row to see its AI summary and biomarker results from that recording.',
    color: '#2D5A8E',
  },
  {
    id: 4,
    icon: <Activity size={28} />,
    title: 'Live Neural Feed & Electrode Map',
    description: 'The Live Neural Feed shows your raw EEG signal streaming in real time. The Electrode Map visualises which brain regions are most active using the standard 10-20 system.',
    tip: 'Colour intensity on the electrode map reflects band-power activity — orange = high activation.',
    color: '#F97316',
  },
  {
    id: 5,
    icon: <BarChart2 size={28} />,
    title: 'Cognitive Metrics & Band Powers',
    description: 'The Cognitive Metrics grid shows key indicators: Focus, Relaxation, Cognitive Load, Attention, and the Theta/Beta Ratio — a validated ADHD and fatigue biomarker.',
    tip: 'Theta/Beta Ratio > 3.0 is clinically significant for inattention. Watch it change as you focus.',
    color: '#2D5A8E',
  },
  {
    id: 6,
    icon: <Cpu size={28} />,
    title: 'AI Cognitive Insights & EEG Analysis',
    description: 'Two AI panels power your analysis: Cognitive Insights generates natural-language brain state interpretations, while AI EEG Analysis provides deep clinical recommendations — both powered by GPT.',
    tip: 'The AI EEG Analysis panel uses a board-certified neurophysiologist system prompt referencing NeuroGuide normative z-scores.',
    color: '#F97316',
  },
  {
    id: 7,
    icon: <FlaskConical size={28} />,
    title: "Alzheimer's Risk Biomarkers",
    description: 'Tracks EEG biomarkers associated with cognitive decline: theta/alpha slowing, coherence drop, and delta intrusion — grounded in peer-reviewed research (Knott, Babiloni, Jeong).',
    tip: 'These are research indicators, not clinical diagnoses. Use them for longitudinal tracking.',
    color: '#EF4444',
  },
  {
    id: 8,
    icon: <Zap size={28} />,
    title: 'ADHD Risk Indicator (Clinical API)',
    description: 'Posts named EEG channels to a real backend ML endpoint every 4 seconds. Returns severity score, risk level, and model confidence — replacing local theta/beta computation with verifiable clinical data.',
    tip: 'Look for the ● REAL API badge — it confirms live backend data. ○ COMPUTED means fallback mode.',
    color: '#F59E0B',
  },
  {
    id: 9,
    icon: <Zap size={28} />,
    title: 'Gamma Band Analysis (30–100 Hz)',
    description: 'Gamma waves are linked to consciousness binding and memory. The 40 Hz gamma band is the focus of MIT GENUS research into Alzheimer\'s prevention via sensory stimulation.',
    tip: 'High gamma activity during tasks indicates strong neural synchrony and cognitive engagement.',
    color: '#A855F7',
  },
  {
    id: 10,
    icon: <Map size={28} />,
    title: 'Functional Connectivity Map',
    description: 'Shows how different brain regions communicate. Arc thickness and brightness represent coherence strength between hemisphere pairs — a key biomarker for cognitive health.',
    tip: 'Coherence below 60% may indicate connectivity decline — a key Alzheimer\'s biomarker.',
    color: '#06B6D4',
  },
  {
    id: 11,
    icon: <Waves size={28} />,
    title: 'Signal Analysis & Channel Quality',
    description: 'Raw waveforms per channel, band-power timelines, and a Channel Quality Grid showing impedance and signal integrity for each electrode in the 10-20 montage.',
    tip: 'Green channels = good contact. Red = poor impedance. Re-seat electrodes if you see red.',
    color: '#10B981',
  },
  {
    id: 12,
    icon: <BookOpen size={28} />,
    title: 'Brain Health Quiz',
    description: 'AI-powered neuroscience questions calibrated to your live cognitive state. Earn XP, build streaks, and learn while your brain is being monitored. Results are saved to your profile.',
    tip: 'Use the ⚙️ settings to adjust difficulty, question types, and EEG calibration bias.',
    color: '#F97316',
  },
  {
    id: 13,
    icon: <Trophy size={28} />,
    title: 'Leaderboard & Streak Tracker',
    description: 'Compete globally with XP earned from quizzes and brain training. Your streak, rank, and XP are persisted to Supabase and updated in real time across all sessions.',
    tip: 'Consistent daily engagement builds streaks — streaks multiply your XP gains.',
    color: '#F59E0B',
  },
  {
    id: 14,
    icon: <Dumbbell size={28} />,
    title: 'NeuroTrainer — AI Brain Rehabilitation',
    description: 'An AI-powered gamified brain training module designed for neuroplasticity improvement, cognitive recovery, and neuroprotection. Exercises adapt to your live EEG state and target specific cognitive domains.',
    tip: 'Each exercise targets a different neural pathway — attention, memory, processing speed, or executive function.',
    color: '#8B5CF6',
  },
  {
    id: 15,
    icon: <Shield size={28} />,
    title: 'Neuroprotection & Rehabilitation Goals',
    description: 'NeuroTrainer tracks your rehabilitation progress across sessions: neuroplasticity score, domain improvements, and AI-generated recovery insights — all grounded in clinical neuroscience.',
    tip: 'Consistent training across multiple sessions is what drives measurable neuroplasticity changes.',
    color: '#8B5CF6',
  },
  {
    id: 16,
    icon: <CheckCircle2 size={28} />,
    title: "You're Ready to Explore!",
    description: 'Your dashboard is live and streaming. All sections update in real time. Start a session, train your brain, explore the AI panels, and track your cognitive health over time.',
    tip: 'Tip: Use the "Dashboard Tour" button (top right) to replay this tour at any time.',
    color: '#10B981',
  },
];

interface OnboardingTourProps {
  onComplete: () => void;
}

export default function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [animDir, setAnimDir] = useState<'forward' | 'back'>('forward');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  const handleNext = useCallback(() => {
    if (isLast) {
      handleFinish();
      return;
    }
    setAnimDir('forward');
    setStep(s => s + 1);
  }, [isLast]);

  const handleBack = useCallback(() => {
    if (step === 0) return;
    setAnimDir('back');
    setStep(s => s - 1);
  }, [step]);

  const handleFinish = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      localStorage.setItem('ibrain_onboarded', '1');
      onComplete();
    }, 400);
  }, [onComplete]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
    if (e.key === 'ArrowLeft') handleBack();
    if (e.key === 'Escape') handleFinish();
  }, [handleNext, handleBack, handleFinish]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const getColorRgb = (color: string) => {
    const map: Record<string, string> = {
      '#F97316': '249,115,22',
      '#2D5A8E': '45,90,142',
      '#EF4444': '239,68,68',
      '#A855F7': '168,85,247',
      '#06B6D4': '6,182,212',
      '#10B981': '16,185,129',
      '#F59E0B': '245,158,11',
      '#8B5CF6': '139,92,246',
    };
    return map[color] || '249,115,22';
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{
        zIndex: 9999,
        background: 'rgba(4,8,15,0.88)',
        backdropFilter: 'blur(8px)',
        opacity: exiting ? 0 : visible ? 1 : 0,
        transition: 'opacity 0.4s ease',
      }}
    >
      {/* Decorative neural lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute h-px"
            style={{
              top: `${15 + i * 14}%`,
              left: 0,
              right: 0,
              background: `linear-gradient(90deg, transparent, rgba(249,115,22,${0.04 + i * 0.01}), transparent)`,
            }}
          />
        ))}
      </div>

      {/* Card */}
      <div
        className="relative w-full max-w-[480px]"
        style={{
          transform: exiting ? 'scale(0.95) translateY(16px)' : 'scale(1) translateY(0)',
          transition: 'transform 0.4s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Top accent */}
        <div
          className="h-[2px] rounded-t-2xl"
          style={{ background: `linear-gradient(90deg, transparent, ${current.color} 40%, ${current.color} 60%, transparent)`, transition: 'background 0.5s ease' }}
        />

        {/* Body */}
        <div
          className="rounded-b-2xl rounded-tr-2xl px-7 py-7"
          style={{
            background: 'linear-gradient(160deg, rgba(11,22,40,0.98) 0%, rgba(6,13,26,0.99) 100%)',
            border: '1px solid rgba(30,58,95,0.7)',
            borderTop: 'none',
            boxShadow: `0 0 60px rgba(249,115,22,0.07), 0 32px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(${getColorRgb(current.color)},0.08)`,
          }}
        >
          {/* Header row */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-bold tabular-nums shrink-0"
                style={{
                  background: `rgba(${getColorRgb(current.color)},0.15)`,
                  border: `1px solid ${current.color}40`,
                  color: current.color,
                  transition: 'all 0.4s ease',
                }}
              >
                {step + 1}
              </div>
              <span className="text-[11px] text-[#4A7AB5] font-medium tracking-wide">
                Step {step + 1} of {TOUR_STEPS.length}
              </span>
            </div>
            <button
              onClick={handleFinish}
              className="flex items-center justify-center w-7 h-7 rounded-lg text-[#4A7AB5] hover:text-white hover:bg-[#1E3A5F]/40 transition-all"
              title="Skip tour"
            >
              <X size={14} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 w-full rounded-full bg-[#1E3A5F]/40 mb-6 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${current.color}99, ${current.color})`,
                boxShadow: `0 0 8px ${current.color}60`,
              }}
            />
          </div>

          {/* Icon + content */}
          <div className="flex flex-col items-center text-center mb-6">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 shrink-0"
              style={{
                background: `linear-gradient(135deg, ${current.color}18 0%, rgba(30,58,95,0.25) 100%)`,
                border: `1px solid ${current.color}30`,
                boxShadow: `0 0 24px ${current.color}18`,
                color: current.color,
                transition: 'all 0.4s ease',
              }}
            >
              {current.icon}
            </div>

            <h2 className="text-[18px] font-bold text-white mb-3 leading-snug">
              {current.title}
            </h2>
            <p className="text-[13.5px] text-[#8BAFD4] leading-relaxed">
              {current.description}
            </p>
          </div>

          {/* Tip box */}
          {current.tip && (
            <div
              className="rounded-xl px-4 py-3 mb-6 flex items-start gap-2.5"
              style={{
                background: `${current.color}0D`,
                border: `1px solid ${current.color}22`,
              }}
            >
              <span className="text-[14px] shrink-0 mt-0.5">💡</span>
              <p className="text-[12px] text-[#8BAFD4] leading-relaxed">{current.tip}</p>
            </div>
          )}

          {/* Step dots */}
          <div className="flex items-center justify-center gap-1 mb-6 flex-wrap max-w-[360px] mx-auto">
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => { setAnimDir(i > step ? 'forward' : 'back'); setStep(i); }}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === step ? '16px' : '5px',
                  height: '5px',
                  background: i === step ? current.color : i < step ? `${current.color}50` : 'rgba(30,58,95,0.6)',
                }}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-medium text-[#8BAFD4] hover:text-white border border-[#1E3A5F]/60 hover:border-[#1E3A5F] hover:bg-[#0F1E35] transition-all"
              >
                <ChevronLeft size={15} />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-bold text-white transition-all duration-200 active:scale-[0.98]"
              style={{
                background: isLast
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : `linear-gradient(135deg, ${current.color} 0%, ${current.color}CC 100%)`,
                boxShadow: isLast
                  ? '0 4px 16px rgba(16,185,129,0.35)'
                  : `0 4px 16px ${current.color}35`,
              }}
            >
              {isLast ? (
                <>
                  <CheckCircle2 size={15} />
                  Start Exploring
                </>
              ) : (
                <>
                  Next
                  <ChevronRight size={15} />
                </>
              )}
            </button>
          </div>

          {/* Keyboard hint */}
          <p className="text-center text-[10px] text-[#1E3A5F] mt-4 tracking-wide">
            ← → arrow keys to navigate · Esc to skip
          </p>
        </div>
      </div>
    </div>
  );
}
